import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { resolveImageUrl } from "@/lib/media";

const lines = ["REBUILDING", "MOVEMENT.", "RESTORING", "DIGNITY."];
const DEFAULT_HERO_IMAGE = "https://images.pexels.com/photos/13538710/pexels-photo-13538710.jpeg";

const lineVariants = {
  hidden: { y: "100%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);

  useEffect(() => {
    api.get("/content/home").then((res) => {
      if (res.data?.hero_image) setHeroImage(res.data.hero_image);
    }).catch(() => {});
  }, []);

  return (
  <section className="relative min-h-screen flex items-center pt-28 pb-16 px-5 md:px-10 overflow-hidden">
    <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <p data-testid="hero-eyebrow" className="text-accent text-sm tracking-[0.25em] uppercase mb-6 font-medium">
          PhysioAbhi Healthcare Foundation · Section 8 Nonprofit
        </p>
        <h1 className="font-hero font-medium text-[13vw] leading-[0.95] lg:text-[6.2vw] tracking-tight">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                initial="hidden"
                animate="visible"
                variants={lineVariants}
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          data-testid="hero-subtitle"
          className="text-muted-foreground text-base md:text-lg mt-8 max-w-lg leading-relaxed"
        >
          Free and subsidised physiotherapy for elderly people, children with special needs,
          and underserved athletes across India — because healing shouldn't be a privilege.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="flex flex-wrap gap-4 mt-10"
        >
          <Button asChild data-testid="hero-donate-button" size="lg" className="rounded-none bg-accent text-accent-foreground hover:bg-[#C25D3D] hover:scale-[1.03] shadow-lg shadow-accent/30 px-10 h-14 text-base font-semibold tracking-wide transition-all group">
            <Link to="/donate">Donate Now <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" /></Link>
          </Button>
          <Button asChild data-testid="hero-programs-button" size="lg" variant="outline" className="rounded-none border-2 border-foreground hover:bg-foreground hover:text-background px-10 h-14 text-base font-semibold tracking-wide transition-colors">
            <Link to="/programs">Our Programs</Link>
          </Button>
        </motion.div>
      </div>

      <div className="lg:col-span-5 relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, rotate: 2 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ rotate: -1 }}
          className="relative aspect-[4/5] overflow-hidden border-4 border-background shadow-2xl"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 92%, 88% 100%, 0 100%)" }}
        >
          <img
            data-testid="hero-image"
            src={resolveImageUrl(heroImage)}
            alt="Physiotherapist adjusting leg strap"
            className="w-full h-full object-cover"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7 }}
          className="absolute -bottom-6 -left-6 md:-left-10 bg-primary text-primary-foreground p-6 w-40 border-2 border-accent"
        >
          <p className="font-heading text-4xl font-semibold text-accent">800+</p>
          <p className="text-xs uppercase tracking-wide mt-1">Lives touched since inception</p>
        </motion.div>
      </div>
    </div>
  </section>
  );
};
